/**
 * @fileoverview Factory function for creating route guards with authentication.
 */

import React, {ComponentType, useEffect} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";

import {AppState} from "../store";

/**
 * Props for route guard components.
 */
type GuardProps = {isAuthenticated: boolean};

/**
 * Configuration options for creating a route guard.
 *
 * @param {Function} authenticatedSelector Function to select authentication state from Redux store.
 * @param {string} redirectPath Path to redirect to when not authenticated.
 * @param {boolean} [redirectToLastPage] Whether to redirect to the last page instead of redirectPath.
 */
interface CreateRouteGuardConfig {
  authenticatedSelector: (state: AppState) => boolean;
  redirectPath: string;
  redirectToLastPage?: boolean;
}

/**
 * Creates a higher-order component that guards routes based on authentication state.
 * Redirects unauthenticated users to a specified path or back to the last page.
 *
 * @param {CreateRouteGuardConfig} config Configuration for the route guard.
 * @returns {Function} A function that takes a component and returns a guarded version.
 */
export function createRouteGuard({
  redirectPath,
  authenticatedSelector,
  redirectToLastPage,
}: CreateRouteGuardConfig) {
  const mapState = (state: AppState) => ({
    isAuthenticated: authenticatedSelector(state),
  });

  /**
   * Note: Returning function so the guard applied with config is properly identified & highlighted as function instead of variable.
   */
  return function Guard<OwnProps>(Component: ComponentType<OwnProps>) {
    /**
     * Redirect component that handles authentication-based routing.
     *
     * @param {OwnProps & GuardProps} props Component props including authentication state.
     * @returns {JSX.Element|null} The guarded component or null if not authenticated.
     */
    function Redirect({isAuthenticated, ...props}: OwnProps & GuardProps) {
      const history = useHistory();
      const location = useLocation<{from?: string}>();

      useEffect(() => {
        const shouldGoBack = redirectToLastPage && location.state?.from;
        if (!isAuthenticated) {
          shouldGoBack
            ? history.goBack()
            : history.push({
                pathname: redirectPath,
                state: {
                  from: location.pathname,
                },
              });
        }
      });

      return isAuthenticated ? (
        <Component {...(props as any as OwnProps)} />
      ) : null;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return connect(mapState)(Redirect);
  };
}
